import axios from 'axios';

import type {
  AssetProcessingActivityDetail,
  CountryVolume,
  HandlingType,
  OwnerAssetProcessingPurposeUpdateInput,
  OwnerCountryHandling,
  RetentionUnit,
} from 'config/api/assetProcessingActivities/assetProcessingActivities.types';
import type { AssetProcessingCountryRow } from 'config/api/assetRopa/assetRopa.types';
import { getRopaCountriesForUi, getRopaCountryClassification } from 'config/dictionaries/ropaCountryMasterdata';

import { RETENTION_UNIT_OPTIONS, type AssetProcessingApaFormValues } from '../_shared/assetProcessingApa.types';

export type AssetProcessingActivityEditorValidationErrors = Partial<Record<string, string>>;

export type DesiredCountryHandling = {
  countryCode: string;
  handlingType: HandlingType;
  volume: CountryVolume;
};

export type CountryHandlingDiff = {
  toCreate: DesiredCountryHandling[];
  toDeleteIds: number[];
};

const isBlank = (value: string) => !value.trim();

const isRetentionValueInvalid = (value: number | null): boolean => value != null && (!Number.isInteger(value) || value < 1);

const HANDLING_FLAGS: Array<{
  flag: keyof Pick<AssetProcessingCountryRow, 'storage' | 'access' | 'support' | 'development'>;
  type: HandlingType;
}> = [
  { flag: 'storage', type: 'storage' },
  { flag: 'access', type: 'access' },
  { flag: 'support', type: 'support' },
  { flag: 'development', type: 'processing' },
];

const isVolume = (value: string): value is CountryVolume => ['low', 'medium', 'high', 'unknown'].includes(value);

const matchesDesiredHandling = (existing: OwnerCountryHandling, desired: DesiredCountryHandling) => {
  if (existing.countryCode !== desired.countryCode) return false;

  if (desired.handlingType === 'processing') {
    return existing.handlingType === 'processing' || existing.handlingType === 'subprocessor';
  }

  return existing.handlingType === desired.handlingType;
};

export const expandCountryRowsToHandlings = (rows: AssetProcessingCountryRow[]): DesiredCountryHandling[] => {
  const result: DesiredCountryHandling[] = [];

  rows.forEach(row => {
    const countryCode = row.countryCode.trim().toUpperCase();
    if (!countryCode) return;

    const volume: CountryVolume = isVolume(row.volume) ? row.volume : 'unknown';

    HANDLING_FLAGS.forEach(({ flag, type }) => {
      if (row[flag]) {
        result.push({ countryCode, handlingType: type, volume });
      }
    });
  });

  return result;
};

export const buildCountryHandlingDiff = (existing: OwnerCountryHandling[], desired: DesiredCountryHandling[]): CountryHandlingDiff => {
  const remaining = [...existing];
  const toCreate: DesiredCountryHandling[] = [];
  const toDeleteIds: number[] = [];

  desired.forEach(desiredItem => {
    const matchIndex = remaining.findIndex(item => matchesDesiredHandling(item, desiredItem));
    if (matchIndex === -1) {
      toCreate.push(desiredItem);
      return;
    }

    const [match] = remaining.splice(matchIndex, 1);
    if (match.volume !== desiredItem.volume) {
      toDeleteIds.push(match.id);
      toCreate.push(desiredItem);
    }
  });

  remaining.forEach(item => {
    toDeleteIds.push(item.id);
  });

  return { toCreate, toDeleteIds };
};

const isRetentionUnit = (value: string): value is Exclude<RetentionUnit, ''> =>
  (RETENTION_UNIT_OPTIONS as readonly string[]).includes(value);

export const mapPurposeApplicationToUpdateInput = (
  purpose: AssetProcessingApaFormValues['purposeApplications'][number],
  revision: number,
): OwnerAssetProcessingPurposeUpdateInput => ({
  revision,
  applicabilityStatus: purpose.applicabilityStatus,
  applicabilityJustification: purpose.justification,
  retentionTrigger: purpose.retentionTrigger,
  retentionValue: purpose.retentionValue,
  ...(isRetentionUnit(purpose.retentionUnit) ? { retentionUnit: purpose.retentionUnit } : {}),
  retentionComment: purpose.retentionComment,
  deletionOrAnonymisationRoutine: purpose.deletionRoutine,
});

export const validatePurposeJustificationsForSave = (
  values: AssetProcessingApaFormValues,
  messages: {
    purposeDoesNotApplyJustification: string;
    required: string;
  },
): AssetProcessingActivityEditorValidationErrors => {
  const errors: AssetProcessingActivityEditorValidationErrors = {};

  values.purposeApplications.forEach((purpose, index) => {
    if (purpose.applicabilityStatus === 'does_not_apply' && isBlank(purpose.justification)) {
      errors[`purpose.${index}.justification`] = messages.purposeDoesNotApplyJustification;
    }

    if (purpose.applicabilityStatus === 'applies' && !isRetentionUnit(purpose.retentionUnit)) {
      errors[`purpose.${index}.retentionUnit`] = messages.required;
    }
  });

  return errors;
};

export const validateAssetProcessingActivityForSubmit = (
  values: AssetProcessingApaFormValues,
  messages: {
    required: string;
    purposeNotAssessed: string;
    purposeAppliesIncomplete: string;
    purposeDoesNotApplyJustification: string;
    securityIncomplete: string;
    securityCommentRequired: string;
    countryRowInvalid: string;
    countriesRequired: string;
    dpiaCommentRequired: string;
    retentionValueMin: string;
  },
): AssetProcessingActivityEditorValidationErrors => {
  const errors: AssetProcessingActivityEditorValidationErrors = {
    ...validatePurposeJustificationsForSave(values, messages),
  };

  if (values.minimumSecurityAssessment === 'not_assessed') {
    errors.minimumSecurityAssessment = messages.required;
  }

  if (['partially_meets', 'does_not_meet'].includes(values.minimumSecurityAssessment) && isBlank(values.minimumSecurityAssessmentComment)) {
    errors.minimumSecurityAssessmentComment = messages.securityCommentRequired;
  }

  values.purposeApplications.forEach((purpose, index) => {
    if (purpose.applicabilityStatus === 'not_assessed') {
      errors[`purpose.${index}.applicabilityStatus`] = messages.purposeNotAssessed;
    }

    if (purpose.applicabilityStatus === 'applies') {
      const missingFields =
        isBlank(purpose.justification) ||
        isBlank(purpose.retentionTrigger) ||
        purpose.retentionValue === null ||
        !isRetentionUnit(purpose.retentionUnit) ||
        isBlank(purpose.deletionRoutine);

      if (missingFields) {
        errors[`purpose.${index}.applies`] = messages.purposeAppliesIncomplete;
      }

      if (!isRetentionUnit(purpose.retentionUnit)) {
        errors[`purpose.${index}.retentionUnit`] = messages.required;
      }

      if (isRetentionValueInvalid(purpose.retentionValue)) {
        errors[`purpose.${index}.retentionValue`] = messages.retentionValueMin;
      }
    }
  });

  values.countryRows.forEach((row, index) => {
    const hasHandling = row.storage || row.access || row.support || row.development;
    if (!hasHandling || isBlank(row.volume) || isBlank(row.countryCode)) {
      errors[`country.${index}`] = messages.countryRowInvalid;
    }
  });

  if (expandCountryRowsToHandlings(values.countryRows).length === 0) {
    errors.countries = messages.countriesRequired;
  }

  if (values.dpiaIndicationStatus === 'indicated' && isBlank(values.dpiaIndicationComment)) {
    errors.dpiaIndicationComment = messages.dpiaCommentRequired;
  }

  return errors;
};

export const hasValidationErrors = (errors: AssetProcessingActivityEditorValidationErrors) => Object.keys(errors).length > 0;

/** Legacy UI adapter for side panel / purpose section until those components are schema-aligned. */
export const mapProcessingActivityToSidePanel = (detail: AssetProcessingActivityDetail) => ({
  name: detail.processingActivity.name,
  description: detail.processingActivity.description,
  processingRole: '',
  dataSubjectCategories: [] as string[],
  personalDataCategories: [] as string[],
  recipientCategories: [] as string[],
  minimumSecurityMeasures: detail.processingActivity.minimumSecurityMeasures
    ? detail.processingActivity.minimumSecurityMeasures
        .split(';')
        .map(item => item.trim())
        .filter(Boolean)
    : [],
  purposes: detail.processingActivity.purposes.map(purpose => ({
    id: purpose.id,
    name: purpose.purposeText,
    legalBasis: purpose.legalBasis,
    legalBasisComment: purpose.legalBasisComment,
    liaRequired: false,
    retentionRule: [purpose.retentionTrigger, purpose.retentionValue, purpose.retentionUnit, purpose.retentionComment]
      .filter(Boolean)
      .join(' '),
  })),
});

/** Maps schema-aligned APA detail into the legacy editor form shape used by current UI sections. */
export const mapDetailToFormValues = (detail: AssetProcessingActivityDetail): AssetProcessingApaFormValues => {
  const purposeNameById = new Map(detail.processingActivity.purposes.map(purpose => [purpose.id, purpose.purposeText]));
  const countryNameByCode = new Map(getRopaCountriesForUi().map(country => [country.iso2, country.name]));

  const countryRowsByCode = new Map<string, AssetProcessingApaFormValues['countryRows'][number]>();

  detail.countries.forEach(country => {
    const existing = countryRowsByCode.get(country.countryCode) ?? {
      id: country.id,
      countryCode: country.countryCode,
      countryName: countryNameByCode.get(country.countryCode) ?? country.countryCode,
      classification: getRopaCountryClassification(country.countryCode),
      storage: false,
      access: false,
      support: false,
      development: false,
      volume: country.volume,
    };

    if (country.handlingType === 'storage') existing.storage = true;
    if (country.handlingType === 'access') existing.access = true;
    if (country.handlingType === 'support') existing.support = true;
    if (country.handlingType === 'processing' || country.handlingType === 'subprocessor') existing.development = true;
    existing.volume = country.volume;
    countryRowsByCode.set(country.countryCode, existing);
  });

  return {
    securityMeasureCategories: [],
    securityComment: detail.processingActivity.minimumSecurityMeasures || '',
    minimumSecurityAssessment: detail.minimumSecurityAssessment,
    minimumSecurityAssessmentComment: detail.minimumSecurityAssessmentComment,
    dpiaIndicationStatus: detail.dpiaIndicationStatus,
    dpiaIndicationComment: detail.dpiaIndicationComment,
    purposeApplications: detail.purposes.map(purpose => ({
      id: purpose.id,
      processingPurposeId: purpose.sourceProcessingPurposeId,
      name: purposeNameById.get(purpose.sourceProcessingPurposeId) ?? '',
      applicabilityStatus: purpose.applicabilityStatus,
      applicationDescription: '',
      justification: purpose.applicabilityJustification,
      retentionTrigger: purpose.retentionTrigger,
      retentionValue: purpose.retentionValue,
      retentionUnit: purpose.retentionUnit,
      retentionComment: purpose.retentionComment,
      deletionRoutine: purpose.deletionOrAnonymisationRoutine,
    })),
    countryRows: Array.from(countryRowsByCode.values()),
  };
};

const APA_REVISION_CONFLICT_CODE = 'asset_processing_activity_revision_conflict';

export const isAssetProcessingActivityRevisionConflict = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return false;
  }

  const { data } = error.response;
  if (!data || typeof data !== 'object' || !('code' in data)) {
    return false;
  }

  return (data as { code?: unknown }).code === APA_REVISION_CONFLICT_CODE;
};

export const isAssetProcessingActivityRevisionValidationError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return false;
  }

  const { data } = error.response;
  return Boolean(data && typeof data === 'object' && 'revision' in data);
};
